import { useMemo } from "react";
import { ROLE_LABELS } from "@/constants/workflow";

const DEPT_ORDER = ["design", "bim", "production", "qaqc", "logistics", "site", "planning"];

const DAY = 86400000;
const COL = 34;
const LABEL = 168;

const PRIORITY_BAR = {
  Low: "bg-slate-400",
  Normal: "bg-cyan-500",
  High: "bg-amber-500",
  Urgent: "bg-red-500",
};

const iso = (date) => date.toISOString().slice(0, 10);
const parse = (value) => new Date(`${value.slice(0, 10)}T00:00:00Z`);
const initials = (name) =>
  name
    .split(" ")
    .map((part) => part[0])
    .slice(0, 2)
    .join("");

/** Gantt-style timeline grouped per person, so workload per assignee is obvious. */
export function TaskTimeline({ tasks, today, canManage, onEdit, hierarchical = false }) {
  const model = useMemo(() => {
    const dated = tasks.filter((t) => t.start_date || t.due_date);
    if (!dated.length) return null;

    const stamps = dated.flatMap((t) => [
      parse(t.start_date || t.due_date).getTime(),
      parse(t.due_date || t.start_date).getTime(),
    ]);
    stamps.push(parse(today).getTime());
    const from = new Date(Math.min(...stamps) - 3 * DAY);
    const to = new Date(Math.max(...stamps) + 3 * DAY);

    const days = [];
    for (let time = from.getTime(); time <= to.getTime(); time += DAY) {
      days.push(new Date(time));
    }
    const months = [];
    days.forEach((day) => {
      const label = day.toLocaleDateString(undefined, { month: "long", year: "numeric" });
      const last = months[months.length - 1];
      if (last && last.label === label) last.span += 1;
      else months.push({ label, span: 1 });
    });

    const bar = (task) => {
      const start = parse(task.start_date || task.due_date);
      const finish = parse(task.due_date || task.start_date);
      return {
        task,
        offset: Math.round((start - from) / DAY),
        span: Math.max(1, Math.round((finish - start) / DAY) + 1),
      };
    };

    const groups = new Map();
    dated.forEach((task) => {
      const people = task.assignees.length ? task.assignees : [{ id: "none", name: "Unassigned" }];
      people.forEach((person) => {
        const depts = hierarchical
          ? person.departments?.length
            ? person.departments
            : ["none"]
          : ["none"];
        depts.forEach((dept) => {
          const key = `${dept}::${person.id}`;
          if (!groups.has(key))
            groups.set(key, {
              key,
              id: person.id,
              name: person.name,
              department: dept,
              rows: [],
            });
          groups.get(key).rows.push(bar(task));
        });
      });
    });
    const people = [...groups.values()].sort((a, b) => {
      if (hierarchical && a.department !== b.department) {
        const ai = DEPT_ORDER.indexOf(a.department);
        const bi = DEPT_ORDER.indexOf(b.department);
        return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
      }
      if (a.id === "none") return 1;
      if (b.id === "none") return -1;
      return a.name.localeCompare(b.name);
    });
    people.forEach((person) => person.rows.sort((a, b) => a.offset - b.offset));

    return {
      days,
      months,
      people,
      todayOffset: Math.round((parse(today) - from) / DAY),
      width: days.length * COL,
    };
  }, [tasks, today, hierarchical]);

  if (!model)
    return (
      <p
        data-testid="timeline-empty"
        className="rounded-xl border border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-500"
      >
        No tasks with a start or due date yet. Add dates to see them on the timeline.
      </p>
    );

  return (
    <div
      data-testid="task-timeline"
      className="okn-scroll overflow-x-auto rounded-xl border border-slate-200 bg-white"
    >
      <div style={{ width: LABEL + model.width, minWidth: "min-content" }}>
        <div className="flex border-b border-slate-100">
          <div
            style={{ width: LABEL }}
            className="mono sticky left-0 z-20 shrink-0 border-r border-slate-200 bg-white px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-slate-500"
          >
            Assignee
          </div>
          {model.months.map((month) => (
            <div
              key={month.label}
              style={{ width: month.span * COL }}
              className="mono shrink-0 border-r border-slate-100 px-2 py-1.5 text-[10px] uppercase tracking-[0.16em] text-slate-500"
            >
              {month.label}
            </div>
          ))}
        </div>

        <div className="flex border-b border-slate-100">
          <div
            style={{ width: LABEL }}
            className="sticky left-0 z-20 shrink-0 border-r border-slate-200 bg-white"
          />
          {model.days.map((day) => {
            const weekend = day.getUTCDay() === 0 || day.getUTCDay() === 6;
            const isToday = iso(day) === today;
            return (
              <div
                key={iso(day)}
                style={{ width: COL }}
                className={`mono shrink-0 py-1 text-center text-[10px] ${
                  isToday
                    ? "bg-cyan-500/10 font-semibold text-cyan-700"
                    : weekend
                      ? "bg-slate-50 text-slate-400"
                      : "text-slate-500"
                }`}
              >
                {day.getUTCDate()}
              </div>
            );
          })}
        </div>

        {model.people.map((person, index) => (
          <div key={person.key}>
            {hierarchical && model.people[index - 1]?.department !== person.department ? (
              <div
                data-testid={`timeline-department-${person.department}`}
                className="mono sticky left-0 border-y border-slate-200 bg-[#EEF2F8] px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-slate-600"
                style={{ width: LABEL + model.width }}
              >
                {ROLE_LABELS[person.department] || "No department"}
              </div>
            ) : null}
          <div
            data-testid={`timeline-person-${person.id}`}
            className="flex border-b border-slate-300"
          >
            <div
              style={{ width: LABEL }}
              className="sticky left-0 z-20 flex shrink-0 items-start gap-2 border-r border-slate-200 bg-white px-3 py-2"
            >
              <span className="mono grid h-6 w-6 shrink-0 place-items-center rounded-full bg-cyan-500/10 text-[9px] text-cyan-700">
                {person.id === "none" ? "—" : initials(person.name)}
              </span>
              <div className="min-w-0">
                <p className="truncate text-[12.5px] font-medium text-slate-900">{person.name}</p>
                <p className="mono text-[9.5px] uppercase tracking-wider text-slate-400">
                  {person.rows.length} task{person.rows.length > 1 ? "s" : ""}
                </p>
              </div>
            </div>

            <div className="relative py-1.5" style={{ width: model.width }}>
              <div
                className="pointer-events-none absolute inset-y-0 z-10 w-[1px] bg-cyan-500/70"
                style={{ left: model.todayOffset * COL + COL / 2 }}
              />
              {person.rows.map(({ task, offset, span }) => {
                const overdue =
                  task.status !== "Done" && task.due_date && task.due_date.slice(0, 10) < today;
                const barWidth = Math.max(span * COL - 4, 12);
                // Short bars cannot hold the text: render the name beside the bar instead of
                // stretching the bar past its real end date.
                const labelOutside = barWidth < 96;
                const tone =
                  task.status === "Done"
                    ? "bg-emerald-500"
                    : overdue
                      ? "bg-red-500"
                      : PRIORITY_BAR[task.priority] || "bg-cyan-500";
                return (
                  <div key={task.id} className="relative h-9">
                    <button
                      data-testid={`timeline-bar-${person.id}-${task.id}`}
                      onClick={() => canManage && onEdit(task)}
                      title={`${task.name} · ${(task.start_date || task.due_date || "").slice(0, 10)} → ${(task.due_date || task.start_date || "").slice(0, 10)}`}
                      style={{ left: offset * COL + 2, width: barWidth }}
                      className={`absolute top-1 flex h-7 items-center gap-1.5 rounded-full text-left text-white transition-opacity hover:opacity-90 ${
                        labelOutside ? "px-0" : "px-3"
                      } ${tone}`}
                    >
                      {labelOutside ? null : (
                        <>
                          <span className="truncate text-[11.5px] font-medium">{task.name}</span>
                          {task.panels?.length ? (
                            <span className="mono shrink-0 rounded bg-white/25 px-1 text-[8.5px]">
                              {task.panels.length}P
                            </span>
                          ) : null}
                        </>
                      )}
                    </button>
                    {labelOutside ? (
                      <span
                        style={{ left: offset * COL + barWidth + 8 }}
                        className="pointer-events-none absolute top-1 flex h-7 items-center whitespace-nowrap text-[11.5px] font-medium text-slate-700"
                      >
                        {task.name}
                        {task.panels?.length ? (
                          <span className="mono ml-1.5 rounded bg-slate-500/10 px-1 text-[8.5px] text-slate-600">
                            {task.panels.length}P
                          </span>
                        ) : null}
                      </span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
          </div>
        ))}
      </div>
    </div>
  );
}
